import { captureEvent } from "@Light/app/analytics";
import type { Query } from "@Light/components/app/QueryLoader";
import { Button } from "@Light/components/page/Button.tsx";
import { ErrorBox } from "@Light/components/page/errors/ErrorBox";
import { FadeScroller } from "@Light/components/page/FadeScroller";
import {
  useHeaderBack,
  useHeaderFloating,
} from "@Light/components/page/header/context";
import { MutationButton } from "@Light/components/page/MutationButton";
import { Skeleton } from "@Light/components/page/Skeleton";
import { useSupportBottomSheet } from "@Light/components/page/SupportBottomSheet";
import { Trust } from "@Light/components/rateOffer/Trust.tsx";
import { useScaffold } from "@Light/scaffold";
import type {
  AlternativePlan,
  PlanResponse,
  PlanValueProp,
} from "@Light/services/lightTypes";
import { formatBrandedPlanName } from "@Light/utils/formatting";
import type { PlanFeatures } from "@Light/utils/planFeatures";
import type { UtilityBehavior } from "@Light/utils/utility";
import { Lightning01, MessageChatCircle } from "@untitledui/icons";
import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { useState, type ReactNode } from "react";
import { AlternativePlans } from "./AlternativePlans";
import type { CreditFocusPresentation } from "./creditFocus";
import { PlanDocuments } from "./PlanDocuments";
import { getPlanHeaderImageForFeatures } from "./planHeaderImage";
import { CalloutTooltip, PlanValueProps } from "./PlanValueProps";
import { RateDetailsBottomSheet } from "./RateDetailsBottomSheet";
import { RateTermToggle } from "./RateTermToggle";

export type RateOfferProps = {
  rate?: PlanResponse;
  features: PlanFeatures;
  planName: string;
  terms: number[];
  selectedTerm: number;
  onSelectTerm: (term: number) => void;
  onContinue: () => void;
  mutation: Query;
  continueButtonText: string;
  continueDisabled?: boolean;
  errorMessage?: string | null;
  onBack?: () => void;
  faqs: ReactNode;
  beforeContinue?: ReactNode;
  valueProps?: PlanValueProp[];
  alternativePlans?: AlternativePlan[] | null;
  onSelectAlternativePlan?: (alternativePlan: AlternativePlan) => void;
  utilityBehavior?: UtilityBehavior;
  creditFocus?: CreditFocusPresentation;
  onMaybeLater?: () => void;
  inlineSupport?: boolean;
};

const RateBadge = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={clsx(
      "text-xs font-medium px-md py-xxs bg-success-25 text-success-700 rounded-full whitespace-nowrap",
      className,
    )}
  >
    {children}
  </div>
);

const PlanDescription = ({
  rate,
  headline,
}: {
  rate?: PlanResponse;
  headline?: string;
}) => {
  if (!rate) {
    return <Skeleton className="m-0.5 h-4 w-full" />;
  }
  const text = headline ?? rate.presentation.headline.headline;
  if (!text) {
    return null;
  }
  return <div className="text-sm text-tertiary">{text}</div>;
};

const RatePrice = ({ rate }: { rate?: PlanResponse }) => {
  if (!rate) {
    return <Skeleton className="mx-auto my-1 h-[30px] w-20" />;
  }
  return (
    <div
      className="text-display-sm font-bold"
      data-testid="rate-display-primary"
    >
      {rate.avg_cents_per_kwh_1000}¢
    </div>
  );
};

const CreditPrice = ({
  creditFocus,
  hasTermToggle,
}: {
  creditFocus: CreditFocusPresentation;
  hasTermToggle: boolean;
}) => {
  const amount = (
    <div
      className="text-display-sm font-bold"
      data-testid="credit-display-primary"
    >
      {creditFocus.calloutText ? (
        <CalloutTooltip
          id="credit-focus-amount"
          text={creditFocus.calloutText}
          className="decoration-2 [text-underline-position:under]"
          // Sits above the term toggle's z-10 options.
          tooltipClassName="z-20"
        >
          {creditFocus.amount}
        </CalloutTooltip>
      ) : (
        creditFocus.amount
      )}
    </div>
  );
  return (
    <>
      <RateBadge className={clsx(hasTermToggle && "mt-md")}>
        Est. annual credits
      </RateBadge>
      <div className="flex flex-col items-center gap-1">
        {amount}
        <div className="text-xs text-secondary">{creditFocus.subtext}</div>
      </div>
    </>
  );
};

export const RateOffer = ({
  rate,
  features,
  planName,
  terms,
  selectedTerm,
  onSelectTerm,
  onContinue,
  mutation,
  continueButtonText,
  continueDisabled,
  errorMessage,
  onBack,
  faqs,
  beforeContinue,
  valueProps,
  alternativePlans,
  onSelectAlternativePlan,
  utilityBehavior,
  creditFocus,
  onMaybeLater,
  inlineSupport,
}: RateOfferProps) => {
  const scaffold = useScaffold();
  const [isViewingDetails, setIsViewingDetails] = useState(false);
  const openSupport = useSupportBottomSheet();

  const renderAlternativePlans = (variant: "main" | "sidebar") => (
    <AnimatePresence initial={false} mode="wait">
      {alternativePlans &&
        alternativePlans.length > 0 &&
        onSelectAlternativePlan && (
          <AlternativePlans
            key={alternativePlans.map((p) => p.plan_group_uuid).join()}
            alternativePlans={alternativePlans}
            onSelect={onSelectAlternativePlan}
            variant={variant}
          />
        )}
    </AnimatePresence>
  );

  useHeaderFloating(true);
  useHeaderBack(onBack);

  const hasTermToggle = terms.length > 1;

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto expanded:overflow-hidden">
      <img
        src={getPlanHeaderImageForFeatures(features)}
        className="h-35.5 sm:h-[calc(100vh-calc(var(--overlay-max-height)-142px+var(--overlay-y-margin)))] sm:max-h-35.5 w-full object-cover"
      />
      <div className="flex-1 min-h-0 w-full flex flex-col expanded:flex-row expanded:h-full">
        <FadeScroller
          className="flex flex-col expanded:h-full expanded:min-h-0 expanded:flex-1"
          contentClassName="flex flex-col p-4xl expanded:p-3xl gap-4xl items-center expanded:h-full expanded:px-7xl expanded:overflow-y-auto"
        >
          {errorMessage && (
            <div data-testid="accept-plan-error">
              <ErrorBox subtitle={errorMessage} />
            </div>
          )}
          <div className="flex flex-1 w-full flex-col items-center gap-4xl text-center">
            <div className="max-w-90 w-full">
              <div className="text-md font-bold text-pretty">
                {formatBrandedPlanName(scaffold.page, planName)}
              </div>
              <PlanDescription rate={rate} headline={creditFocus?.headline} />
            </div>
            <div className="flex flex-col items-center gap-1">
              {hasTermToggle && (
                <RateTermToggle
                  terms={terms}
                  selected={selectedTerm}
                  onChange={onSelectTerm}
                />
              )}
              {creditFocus ? (
                <CreditPrice
                  creditFocus={creditFocus}
                  hasTermToggle={hasTermToggle}
                />
              ) : (
                <>
                  {!hasTermToggle && terms.length === 1 && terms[0] > 1 && (
                    <RateBadge>Low rate fixed for {terms[0]} months</RateBadge>
                  )}
                  <div>
                    <RatePrice rate={rate} />
                    <div className="text-xs text-secondary">
                      per kWh at 1000 kWh
                    </div>
                  </div>
                </>
              )}
              <button
                className="text-xs text-tertiary underline"
                onClick={() => {
                  setIsViewingDetails(!isViewingDetails);
                  captureEvent("plan_details_expanded");
                }}
              >
                View details
              </button>
            </div>
          </div>
          <PlanValueProps
            valueProps={
              creditFocus?.valueProps ??
              valueProps ??
              rate?.presentation.value_propositions ??
              []
            }
            isLoading={!rate}
          />
          {renderAlternativePlans("main")}
          <div
            className={clsx(
              "flex flex-col gap-lg mt-auto w-full",
              onMaybeLater ? "max-w-104" : "max-w-90",
            )}
          >
            {beforeContinue}
            <div
              className={clsx(
                "grid gap-md",
                onMaybeLater && "expanded:grid-cols-2",
              )}
            >
              <MutationButton
                size="md"
                className="w-full"
                mutation={mutation}
                mutate={onContinue}
                disabled={continueDisabled}
                mutateButtonText={continueButtonText}
                data-testid="accept-rate-button"
              />
              {onMaybeLater && (
                <Button
                  size="md"
                  buttonType="outline"
                  className="expanded:order-first"
                  onClick={onMaybeLater}
                  data-testid="maybe-later-button"
                >
                  Maybe later
                </Button>
              )}
            </div>
            {inlineSupport ? (
              <div className="text-sm font-medium text-primary text-center">
                Have a question?{" "}
                <button className="underline" onClick={openSupport}>
                  Call or email us
                </button>
              </div>
            ) : (
              <Button buttonType="outline" onClick={openSupport}>
                <MessageChatCircle width={20} height={20} />
                Call or email us
              </Button>
            )}
          </div>
          <div className="expanded:hidden">
            <Trust utility={utilityBehavior} />
          </div>
        </FadeScroller>
        <div className="flex flex-col pt-4xl px-xl bg-secondary expanded:shrink-0 expanded:w-77 expanded:pt-0 expanded:pb-0">
          <div className="px-lg pb-3xl hidden expanded:order-2 expanded:block">
            <Trust utility={utilityBehavior} />
          </div>
          <FadeScroller
            className="flex flex-col expanded:flex-1 expanded:min-h-0 expanded:order-1"
            contentClassName="flex flex-col gap-4xl py-4xl expanded:overflow-y-auto"
          >
            <PlanDocuments isLoading={!rate} />
            {faqs}
            {renderAlternativePlans("sidebar")}
          </FadeScroller>
        </div>
      </div>
      {rate && (
        <RateDetailsBottomSheet
          rate={rate}
          open={isViewingDetails}
          onClose={() => {
            setIsViewingDetails(false);
          }}
        />
      )}
    </div>
  );
};

export const RateOfferHint = ({ children }: { children: ReactNode }) => (
  <div className="hidden justify-center items-center gap-md expanded:flex">
    <Lightning01 className="size-4 text-fg-secondary" />
    <span className="text-sm text-tertiary">{children}</span>
  </div>
);
